import React, { useState } from 'react';
import { routes, TYPES } from 'constants/config';
import { Button, ButtonGroup } from 'ui';
import Select from 'components/shared/select';
import request from 'utils/request_ajax';
import RemoteAutoSuggest from '../TriggerBuilder/TriggerField/RemoteAutoSuggest';

const EditTrigger = ({
  trigger,
  cancelEdit,
  receiveNewTrigger,
  receiveEditedTrigger,
}) => {
  const isEditing = !!trigger.id;
  const [formValues, updateFormValues] = useState({});

  const updateContentField = fieldName => (e, input) => {
    updateFormValues({
      ...formValues,
      [fieldName + '_id']: input.id,
      [fieldName + '_type']: input.type,
      [fieldName + '_name']: input.name,
      [fieldName + '_title']: input.title,
    });
  };

  const updateFormField = (fieldName, inputTransform) => e => {
    updateFormValues({
      ...formValues,
      [fieldName]: inputTransform(e.target.value),
    });
  };

  const hasValidInputs = () => {
    return [
      'assignee_id',
      'assignee_type',
      'triggerable_id',
      'triggerable_type',
      'assignable_id',
      'assignable_type',
      // eslint-disable-next-line
    ].every(key => formValues.hasOwnProperty(key));
  };

  const saveTrigger = () => {
    if (isEditing) {
      request
        .patchJSON(routes.TRIGGER(trigger.id), {
          ...trigger,
          ...formValues,
        })
        .then(({ success }) => {
          if (success) {
            cancelEdit();
            receiveEditedTrigger({ ...trigger, ...formValues });
          }
        })
        .catch(err => {
          console.error(err);
        });
    } else {
      request
        .postJSON(routes.TRIGGERS.INDEX, formValues)
        .then(res => {
          cancelEdit();
          receiveNewTrigger(res);
        })
        .catch(err => {
          console.error(err);
        });
    }
  };

  return (
    <div
      id={`trigger_${isEditing ? trigger.id : 'new'}`}
      className="EditTrigger"
    >
      <div className="EditTrigger-row">
        {t('triggers.labels.assignee')}
        <section>
          <RemoteAutoSuggest
            name="assignee"
            placeholder={t('triggers.assignee_placeholder')}
            url={routes.ASSIGNABLES.ASSIGNEES}
            onSuggestionSelected={updateContentField('assignee')}
            {...(isEditing && { defaultValue: trigger.assignee_name })}
          />
        </section>
        {trigger.triggerable_type === TYPES.GROUP ? (
          <>
            {t('triggers.labels.group_add')}
            {t('triggers.labels.member_of')}
            <section>
              <RemoteAutoSuggest
                name="triggerable"
                placeholder={t('groups.group_name')}
                url={routes.ASSIGNABLES.GROUP_ASSIGNEES}
                onSuggestionSelected={updateContentField('triggerable')}
                {...(isEditing && { defaultValue: trigger.triggerable_title })}
              />
            </section>
          </>
        ) : (
          <>
            {t('triggers.labels.completable')}
            <section>
              <RemoteAutoSuggest
                name="triggerable"
                placeholder={t('common.assignable')}
                suggestionNameKey="title"
                url={routes.ASSIGNABLES.CONTENT}
                onSuggestionSelected={updateContentField('triggerable')}
                {...(isEditing && { defaultValue: trigger.triggerable_title })}
              />
            </section>
          </>
        )}
        {t('triggers.labels.assignable')}
        <section>
          <RemoteAutoSuggest
            name="assignable"
            placeholder={t('common.assignable')}
            suggestionNameKey="title"
            url={routes.ASSIGNABLES.CONTENT}
            onSuggestionSelected={updateContentField('assignable')}
            {...(isEditing && { defaultValue: trigger.assignable_title })}
          />
        </section>
        {t('triggers.due.in')}
        <section>
          <input
            type="number"
            name="dueInDays"
            step="1"
            min="0"
            className="NewInput NewInput--light w-60 m-0-2"
            onChange={updateFormField('due_in_days', val =>
              val.trim() !== '' ? parseInt(val, 10) : null,
            )}
            {...(isEditing && { defaultValue: trigger.due_in_days || 0 })}
          />
        </section>
        {t('triggers.due.days')}
        <section>
          <Select
            onChange={updateFormField('enabled', val => val === 'true')}
            {...(isEditing && { defaultValue: trigger.enabled })}
          >
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
        </section>
      </div>
      <div className="EditTrigger-row">
        <section>
          {t('triggers.reassign.label')}
          <Select
            onChange={updateFormField('reassign', val => val === 'true')}
            {...(isEditing && { defaultValue: trigger.reassign })}
          >
            <option value="true">
              {t('triggers.reassign.will_be_restarted')}
            </option>
            <option value="false">
              {t('triggers.reassign.will_not_be_restarted')}
            </option>
          </Select>
        </section>
        <section>
          <ButtonGroup>
            <Button onClick={cancelEdit}>{t('triggers.cancel')}</Button>
            <Button
              onClick={saveTrigger}
              disabled={!isEditing && !hasValidInputs()}
              primary
            >
              {t(`triggers.${isEditing ? 'save_trigger' : 'create_trigger'}`)}
            </Button>
          </ButtonGroup>
        </section>
      </div>
    </div>
  );
};

export default EditTrigger;
